/**
 * The retention window, the cause redaction, and the erasure receipts.
 *
 * Tenant erasure is deliberately absent, and the screen says so: erase_tenant returns
 * blob keys the caller must delete and the blob store has no delete operation, so a
 * button here would half-erase. A console that fakes a feature costs more trust than one
 * that admits a gap.
 */
import { useState, type ReactElement } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get, patch, post } from "../lib/api";
import { stamp } from "../lib/format";
import type { ErasureReceipt, RetentionState } from "../lib/types";
import { Shell, Sep } from "../components/Shell";
import { DataTable } from "../components/DataTable";
import { Button, Empty, Failed, Loading, Panel, Tile } from "../components/ui";

const CONFIRMATION = "redact";

export function Retention(): ReactElement {
  const queryClient = useQueryClient();
  const [indefinite, setIndefinite] = useState(true);
  const [days, setDays] = useState("");
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<string | null>(null);

  const state = useQuery({
    queryKey: ["retention"],
    queryFn: async () => {
      const loaded = await get<RetentionState>("/v1/retention");
      setIndefinite(loaded.retention_days === null);
      setDays(loaded.retention_days === null ? "" : String(loaded.retention_days));
      return loaded;
    },
  });

  const fail = (cause: unknown) =>
    setError(cause instanceof Error ? cause.message : String(cause));
  const done = () => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ["retention"] });
  };

  const save = useMutation({
    mutationFn: () =>
      patch<RetentionState>("/v1/retention", {
        retention_days: indefinite ? null : Number(days),
      }),
    onSuccess: done,
    onError: fail,
  });

  const redact = useMutation({
    mutationFn: () => post<{ redacted_rows: number }>("/v1/retention/redact-causes", {}),
    onSuccess: () => {
      setTyped("");
      done();
    },
    onError: fail,
  });

  return (
    <Shell crumb={<>settings <Sep /> retention</>} fill>
      {error && (
        <p className="border border-crit/40 bg-crit-wash px-3 py-2 text-[12.5px] text-crit">
          {error}
        </p>
      )}

      {state.isPending ? (
        <Loading what="retention" />
      ) : state.error ? (
        <Failed error={state.error} what="retention" />
      ) : (
        <>
          <Panel title="Window">
            <form
              className="flex flex-wrap items-end gap-4 p-4"
              onSubmit={(event) => {
                event.preventDefault();
                save.mutate();
              }}
            >
              <label className="flex items-center gap-2 text-[13px]">
                <input
                  type="checkbox"
                  checked={indefinite}
                  onChange={(event) => setIndefinite(event.target.checked)}
                />
                Keep indefinitely
              </label>
              <div>
                <label htmlFor="days" className="eyebrow">
                  // days
                </label>
                <input
                  id="days"
                  type="number"
                  min={1}
                  disabled={indefinite}
                  value={days}
                  onChange={(event) => setDays(event.target.value)}
                  className="mt-2 w-28 border border-hair bg-ground px-3 py-2 font-mono text-[13px] outline-none focus:border-gold disabled:opacity-40"
                />
              </div>
              <Button variant="primary" type="submit" disabled={save.isPending}>
                save
              </Button>
              <p className="max-w-[52ch] text-[12px] leading-relaxed text-dim">
                Keeping indefinitely is a decision someone has to make, not a default that
                quietly accumulates liability.
              </p>
            </form>
          </Panel>

          <Panel title="Redact free-text detail">
            <div className="flex flex-wrap items-end gap-4 p-4">
              <Tile label="rows affected" value={state.data.redactable_rows} />
              <div>
                <label htmlFor="confirm" className="eyebrow">
                  // type {CONFIRMATION} to confirm
                </label>
                <input
                  id="confirm"
                  value={typed}
                  onChange={(event) => setTyped(event.target.value)}
                  className="mt-2 w-40 border border-hair bg-ground px-3 py-2 font-mono text-[13px] outline-none focus:border-gold"
                />
              </div>
              <Button
                disabled={typed !== CONFIRMATION || redact.isPending}
                onClick={() => redact.mutate()}
              >
                redact causes
              </Button>
              <p className="max-w-[52ch] text-[12px] leading-relaxed text-dim">
                Removes the free-text detail from every row that has one, keeping every
                amount and date. It cannot be undone.
              </p>
            </div>
          </Panel>

          <Panel title="Erasures" fill>
            {state.data.erasures.length === 0 ? (
              <Empty
                title="No erasures"
                hint="Erasing an entire tenant is a runbook operation, not a button here — see docs/runbook.md."
              />
            ) : (
              <DataTable
                fill
                table="erasures"
                rows={state.data.erasures}
                rowKey={(receipt: ErasureReceipt) => receipt.erasure_id}
                columns={[
                  {
                    key: "at",
                    header: "When",
                    width: 190,
                    cellClass: "font-mono",
                    render: (r) => stamp(r.completed_at),
                  },
                  { key: "documents", header: "Documents", width: 130, align: "end", render: (r) => r.documents },
                  { key: "rows", header: "Rows", width: 130, align: "end", render: (r) => r.rows_erased },
                  { key: "blobs", header: "Blobs listed", width: 150, align: "end", render: (r) => r.blobs_listed },
                  {
                    key: "id",
                    header: "Receipt",
                    width: 300,
                    cellClass: "font-mono",
                    render: (r) => r.erasure_id,
                  },
                ]}
              />
            )}
            <p className="border-t border-hair px-4 py-3 text-[12px] leading-relaxed text-dim">
              Erasing an entire tenant is a runbook operation. The stored bytes are listed,
              not deleted, by the erasure itself — the column is honestly named
              &ldquo;blobs listed&rdquo; — so removing them is a documented step rather than
              a button that would only half-erase.
            </p>
          </Panel>
        </>
      )}
    </Shell>
  );
}
